import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Button, Loader } from "semantic-ui-react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { t } from "../../../constant/translations";
import { reverse, routes } from "../../../routes";
import { device } from "../../../config";
import { BackButton } from "../../common/back-button";
import { fetchSurvey } from "../../../redux/modules/survey";
import {
  castSurveyVote,
  updateSurveyVote,
  fetchMyVote,
} from "../../../redux/modules/survey-vote";
// ─── Layout ──────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;

  @media ${device.desktop} {
    flex-direction: row;
    height: calc(100vh - 56px);
    overflow: hidden;
  }
`;

const scrollbarStyle = `
  scrollbar-width: thin;
  scrollbar-color: #d6d3d1 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d6d3d1;
    border-radius: 4px;
  }
`;

const VoteColumn = styled.div`
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
  ${scrollbarStyle}

  @media ${device.desktop} {
    padding: 20px 28px;
    max-width: 600px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

// ─── Vote form styles ─────────────────────────────────────────────────────────

const WarnBox = styled.div`
  background: #fafaf9;
  border: 1px solid #e7e5e4;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 12px;
  color: #78716c;
  margin-bottom: 14px;
`;

const QuorumBar = styled.div`
  background: #fafaf9;
  border: 1px solid #e7e5e4;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 14px;
`;

const QuorumBarOuter = styled.div`
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin: 6px 0 4px;
`;

const QuorumBarInner = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${({ reached }) => (reached ? "#28a745" : "#1a7fc1")};
  width: ${({ pct }) => pct}%;
`;

const QuorumMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #888;
`;

const QuorumHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
`;

const QuorumStatus = styled.span`
  font-size: 11px;
  color: ${({ reached }) => (reached ? "#16a34a" : "#92400e")};
  font-weight: 500;
`;

const SurveyTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 14px;
`;

const QuestionCard = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const QuestionHeader = styled.div`
  padding: 10px 14px 6px;
  background: #f8f9fb;
  border-bottom: 1px solid #e9ecef;
`;

const QuestionNum = styled.div`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
`;

const QuestionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1c1917;
`;

const QuestionNote = styled.div`
  font-size: 10px;
  color: #888;
  font-style: italic;
  margin-top: 3px;
`;

const QuestionBody = styled.div`
  padding: 10px 14px;
`;

const VoteOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid ${({ selected }) => (selected ? "#93c5fd" : "#e7e5e4")};
  border-radius: 4px;
  margin-bottom: 6px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? "#f0f7ff" : "#fff")};
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #93c5fd;
    background: #f8fbff;
  }
`;

const OptionLabel = styled.span`
  font-size: 13px;
  color: #1c1917;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
`;

const ProgressNote = styled.span`
  font-size: 11px;
  color: #aaa;
`;

const AlreadyVotedBanner = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 8px 14px;
  font-size: 12px;
  color: #15803d;
  margin-bottom: 14px;
`;

// ─── Page component ───────────────────────────────────────────────────────────

function VotePageComponent({
  match,
  history,
  survey,
  loading,
  fetchSurvey,
  castSurveyVote,
  updateSurveyVote,
  fetchMyVote,
}) {
  const blockId = parseInt(match.params.blockId);
  const surveyId = parseInt(match.params.surveyId);

  const [answers, setAnswers] = useState({});
  const [existingVotes, setExistingVotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSurvey(surveyId);
    fetchMyVote(surveyId).then((res) => {
      if (res && res.body && res.body.length > 0) {
        setExistingVotes(res.body);
        setAnswers(res.body[0].answers || {});
      }
    });
  }, [surveyId]);

  if (loading || !survey)
    return <Loader active inline="centered" size="small" />;

  const questions = survey.questions || [];
  const allAnswered = questions.every((q) => answers[q.id]);

  const votedWeightPct = parseFloat(survey.voted_weight_pct || 0);
  const quorumReached = votedWeightPct >= 26;
  const deadline = survey.end_date
    ? new Date(survey.end_date).toLocaleDateString("bg-BG")
    : "—";

  const handleSelect = (questionId, option) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
  };

  const handleSubmit = () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    const stringAnswers = {};
    Object.entries(answers).forEach(([k, v]) => {
      stringAnswers[String(k)] = v;
    });
    const action =
      existingVotes.length > 0
        ? updateSurveyVote({
            id: existingVotes[0].id,
            survey: surveyId,
            answers: stringAnswers,
          })
        : castSurveyVote({ survey: surveyId, answers: stringAnswers });
    action
      .then(() => {
        setSubmitting(false);
        toast.success(t("survey.vote_recorded"));
        history.push(reverse(routes.blockSurveys, { blockId }));
      })
      .catch(() => setSubmitting(false));
  };

  const handleBack = () =>
    history.push(reverse(routes.blockSurveys, { blockId }));

  return (
    <PageWrap>
      <VoteColumn>
        <PageHeader>
          <BackButton size="tiny" />
        </PageHeader>

        <QuorumBar>
          <QuorumHeader>
            <span>{t("survey.participation")}</span>
            <QuorumStatus reached={quorumReached}>
              {quorumReached
                ? `\u2713 ${t("survey.quorum_reached")}`
                : t("survey.quorum_not_reached")}
              {" \u00b7 "}
              {t("survey.end_date")}: {deadline}
            </QuorumStatus>
          </QuorumHeader>
          <QuorumBarOuter>
            <QuorumBarInner
              pct={Math.min(votedWeightPct, 100)}
              reached={quorumReached}
            />
          </QuorumBarOuter>
          <QuorumMeta>
            <span>0%</span>
            <span
              style={{
                color: quorumReached ? "#16a34a" : "#92400e",
                fontWeight: 500,
              }}
            >
              {votedWeightPct.toFixed(1)}% {t("survey.weight_pct")}
            </span>
            <span>{t("survey.quorum_rule")}</span>
          </QuorumMeta>
        </QuorumBar>

        {existingVotes.length > 0 && (
          <AlreadyVotedBanner>{t("survey.already_voted")}</AlreadyVotedBanner>
        )}

        <WarnBox>{t("survey.not_anonymous")}</WarnBox>

        <SurveyTitle>{survey.title}</SurveyTitle>

        {questions.map((q, qi) => {
          const opts =
            q.qtype === "yesno"
              ? [t("survey.yes"), t("survey.no"), t("survey.abstain")]
              : q.options || [];
          return (
            <QuestionCard key={q.id}>
              <QuestionHeader>
                <QuestionNum>
                  {t("survey.question_num")} {qi + 1} {t("survey.of")}{" "}
                  {questions.length}
                </QuestionNum>
                <QuestionTitle>{q.title}</QuestionTitle>
                {q.qtype === "choose" && (
                  <QuestionNote>
                    {t("survey.winner")} — {t("survey.winner_note")}
                  </QuestionNote>
                )}
              </QuestionHeader>
              <QuestionBody>
                {opts.map((opt) => (
                  <VoteOption
                    key={opt}
                    selected={answers[q.id] === opt}
                    onClick={() => handleSelect(q.id, opt)}
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={answers[q.id] === opt}
                    />
                    <OptionLabel>{opt}</OptionLabel>
                  </VoteOption>
                ))}
              </QuestionBody>
            </QuestionCard>
          );
        })}

        <FooterRow>
          <ProgressNote>
            {Object.keys(answers).length} / {questions.length}{" "}
            {t("survey.questions").toLowerCase()}
          </ProgressNote>
          <Button
            positive
            disabled={!allAnswered}
            loading={submitting}
            onClick={handleSubmit}
          >
            {existingVotes.length > 0
              ? t("survey.edit_vote")
              : t("survey.confirm_vote")}
          </Button>
        </FooterRow>
      </VoteColumn>
    </PageWrap>
  );
}

const mapStateToProps = (state, ownProps) => {
  const surveyId = parseInt(ownProps.match?.params?.surveyId);
  return {
    survey: (state.survey.entities || []).find((s) => s.id === surveyId),
    loading: state.survey.loading,
  };
};

export const VotePage = withRouter(
  connect(mapStateToProps, {
    fetchSurvey,
    castSurveyVote,
    updateSurveyVote,
    fetchMyVote,
  })(VotePageComponent)
);
